import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Dimmer, Loader, Message, Modal } from 'semantic-ui-react';

import {
    SocialOption,
    HelloOption,
    neighborsHomeGridOptions,
    getVisibleNeighborsOptions,
    BLUE_BAR,
} from '../../../components/components/CustomSidebar/NeighborsOptions';
import { NeighborsHomeOption } from '../../types/NeighborsOption';
import News from '../../components/Home/News';
import Status from '../../components/Home/Status';
import Greeting from '../../components/Home/Greeting';
import UserContext from '../../../contexts/UserContext';
import * as CheckIn from '../../services/CheckIn';
import { CheckInPayload } from '../../types/CheckIn';
import sendToast from '../../../utils/Toast';
import { useFacility } from '../../contexts/facility/FacilityContext';
import useDefaultNeighborsLanding from '../../hooks/useDefaultNeighborsLanding';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import useGreetingsSummary from '../../hooks/useGreetingsSummary';
import { getCheckInOutcome } from '../../utils/checkInGreeting';
import CheckInNotice from '../../components/CheckInNotice';
import { StarIcon } from '../../components/icons';
import { getResidentDetails } from '../../services/MyProfile';
import { updateResidentPremise } from '../../services/Resident';
import { getSignOutQuestions, addSignOutAnswer } from '../../services/SignOutQuestions';
import { getEnabledSignOutQuestions } from '../../utils/signOutQuestions';
import { SignOutAnswers, SignOutQuestionItem } from '../../types/SignOutQuestions';
import SignOutQuestionsModal from '../../components/SignOutQuestionsModal';
import { getResidentDisplayFirstName } from '../../utils/residentIdentity';
import './style.less';

// Filled heart used by the Greetings badge and the upsell band (the stroke-only
// GreetingsIcon reads too light at this size).
const HeartGlyph: React.FC<{ size: number }> = ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
);

const NeighborsHome = () => {
    const { userObject } = useContext(UserContext);
    const history = useHistory();
    const {
        resolvedShowPaymentsEntry,
        facilityPaymentsEnabled,
        facilityLookupFailed,
        loading: paymentAccessLoading,
    } = useNeighborsPaymentAccess();
    const {
        registrants: { list: listOfResidents, activeRegistrantsIndexes },
    } = userObject;

    const activeRegistrantIndex = activeRegistrantsIndexes[0];
    const activeResident = typeof activeRegistrantIndex === 'number' ? listOfResidents[activeRegistrantIndex] : undefined;
    // Greetings has no tile of its own: the Hello! tile carries it (see renderTile), and
    // the /greetings page stays reachable from the sidebar and the tile. The page itself
    // decides between the subscribe upsell and the full page from actual add-on
    // entitlement, not a resident-toggleable profile flag.
    const visibleHomeGridOptions = getVisibleNeighborsOptions(
        neighborsHomeGridOptions,
        userObject?.userType,
        resolvedShowPaymentsEntry,
    );

    const [checkInStatus, setCheckInStatus] = useState<boolean>(false);
    const [residentCheckInStatusObj, setResidentCheckInStatusObj] = useState<CheckInPayload>();
    const [isCheckInStatusLoading, setIsCheckInStatusLoading] = useState(false);
    const [isFetchingStatus, setIsFetchingStatus] = useState(false);
    const [showCheckInConfirm, setShowCheckInConfirm] = useState(false);
    // Why the family wasn't notified on a check-in that otherwise worked: never signed up, or
    // subscribed but Greetings is switched off (e.g. a cancellation waiting for period end).
    const [greetingNotice, setGreetingNotice] = useState<'not_signed_up' | 'switched_off' | null>(null);
    const { facilityTimezone: facilityTimeZone, loading: isFetchingFacilityTimeZoneLoading } = useFacility();
    const { isSocialDefault, isSaving: isSavingDefaultLanding, toggleSocialDefault } = useDefaultNeighborsLanding();
    // Daily Greetings is sold through card payments, so a facility with payments off gets the plain
    // Hello! tile: no lookups, and never an upsell. Guests have no Hello! tile at all. The answer is
    // the same one the side menu and the Greetings page use (the shared payments-access result).
    // undefined = not known yet, or the lookup failed: the tile keeps the answer it saved last time
    // instead of clearing it, and the Greetings lookups start once the answer is in.W
    const isGuest = userObject?.userType === 'guest';
    let greetingsEnabled: boolean | undefined;
    if (isGuest) {
        greetingsEnabled = false;
    } else if (!paymentAccessLoading && !facilityLookupFailed) {
        greetingsEnabled = facilityPaymentsEnabled;
    }
    // Until the facility answer is in, a press could run the plain check-in from a tile that is about to
    // become the Greetings one. (A failed lookup is not waited on: the check-in itself is free.)
    const greetingsAnswerPending = !isGuest && paymentAccessLoading;
    const greetingsSummary = useGreetingsSummary(
        activeResident?._id ? String(activeResident._id) : undefined,
        activeResident?.Facility ? String(activeResident.Facility) : undefined,
        greetingsEnabled,
    );
    // Only the resident's own login (managerType 'Resident'): the feed shows a change without staff details
    // as the resident's own, so a family manager or a guest must not get this tile.
    const canTogglePremise =
        !isGuest && activeResident?.managerType === 'Resident' && !!activeResident?._id && !!activeResident?.Facility;
    // undefined = not known (loading, or the read failed); otherwise a boolean (an unset onPremise means on premise).
    const [onPremise, setOnPremise] = useState<boolean | undefined>();
    const [isPremiseUpdating, setIsPremiseUpdating] = useState(false);
    const [isPremiseFetching, setIsPremiseFetching] = useState(canTogglePremise);
    const isPremiseBusy = isPremiseUpdating || isPremiseFetching;
    // Read synchronously by the handlers: the busy state above only updates on the next render.
    const premiseLock = useRef(false);
    // The sign-out answers are saved before the status changes. Once saved they are final: the modal locks and
    // a retry only repeats the status change.
    const [answersSaved, setAnswersSaved] = useState(false);

    // The current status, or undefined when it can't be read (already reported with a toast).
    const readPremise = async (): Promise<boolean | undefined> => {
        try {
            const resident = await getResidentDetails(String(activeResident.Facility), String(activeResident._id));
            return resident.onPremise !== false;
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to load your status');
            return undefined;
        }
    };

    useEffect(() => {
        if (!canTogglePremise) return;
        (async () => {
            setIsPremiseFetching(true);
            setOnPremise(await readPremise());
            setIsPremiseFetching(false);
        })();
    }, [canTogglePremise, activeResident?._id, activeResident?.Facility]);

    // Sign-out questions the facility has set up, shown before a resident is marked as out (the room
    // device asks the same ones). null when none are set up, or while no sign-out is waiting on them.
    const [signOutPrompt, setSignOutPrompt] = useState<{ questionsId: string; items: SignOutQuestionItem[] } | null>(null);

    // A failed lookup must not stop a resident from marking themselves out (the room device skips the
    // questions the same way), so it counts as "no questions".
    const loadSignOutPrompt = async (facilityId: string) => {
        try {
            const record = await getSignOutQuestions(facilityId);
            const items = getEnabledSignOutQuestions(record);
            return record && items.length ? { questionsId: record._id, items } : null;
        } catch (error) {
            console.error('[loadSignOutPrompt] Could not load sign-out questions, signing out without them', { facilityId, error });
            sendToast('error', error instanceof Error ? error.message : 'Failed to load sign-out questions');
            return null;
        }
    };

    const handlePremiseClick = async () => {
        if (isPremiseBusy || premiseLock.current) return;
        premiseLock.current = true;
        setIsPremiseUpdating(true);
        try {
            // The API sets an absolute value, so decide from the status as it is now, not as it was on load
            // (it can also have been unknown: then this tap only loads it).
            const current = await readPremise();
            if (current === undefined) return;
            if (current !== onPremise) {
                setOnPremise(current);
                if (onPremise !== undefined) sendToast('info', current ? "You're already marked as on premise" : "You're already marked as off premise");
                return;
            }
            const next = !current;
            if (!next) {
                // Going out: ask the facility's sign-out questions first. The modal finishes the sign-out.
                const prompt = await loadSignOutPrompt(String(activeResident.Facility));
                if (prompt) {
                    setAnswersSaved(false);
                    setSignOutPrompt(prompt);
                    return;
                }
            }
            await updateResidentPremise(String(activeResident.Facility), String(activeResident._id), next);
            setOnPremise(next);
            sendToast('success', next ? "You're marked as on premise" : "You're marked as off premise");
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to update your status');
        } finally {
            premiseLock.current = false;
            setIsPremiseUpdating(false);
        }
    };

    const handleSignOutSubmit = async (answers: SignOutAnswers) => {
        if (!signOutPrompt || premiseLock.current) return;
        premiseLock.current = true;
        const facilityId = String(activeResident.Facility);
        const residentId = String(activeResident._id);
        setIsPremiseUpdating(true);
        try {
            // Answers first: a stale or rejected questionnaire fails before the status changes, so the resident is
            // never left marked out without them. If the status change then fails, a retry only repeats that step.
            if (!answersSaved) {
                await addSignOutAnswer(facilityId, {
                    signOutQuestionId: signOutPrompt.questionsId,
                    residentId,
                    questions: signOutPrompt.items.map(({ key }) => ({ questionKey: key, answerText: (answers[key] ?? '').trim() })),
                });
                setAnswersSaved(true);
            }
            await updateResidentPremise(facilityId, residentId, false);
            setOnPremise(false);
            setSignOutPrompt(null);
            sendToast('success', "You're marked as off premise");
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to update your status');
        } finally {
            premiseLock.current = false;
            setIsPremiseUpdating(false);
        }
    };

    const closeSignOutPrompt = () => {
        if (!premiseLock.current && !answersSaved) setSignOutPrompt(null);
    };

    const closeGreetingNotice = useCallback(() => setGreetingNotice(null), []);

    // `quiet` keeps a failed read from showing its own error toast, for a caller that reports it itself.
    const fetchCheckInStatus = async (facilityTimeZone: string, quiet = false): Promise<boolean> => {
        try {
            const response = await CheckIn.fetchCurrentCheckInStatusOfResident(
                activeResident.Facility,
                String(activeResident._id),
                facilityTimeZone,
            );
            const result = response?.Result?.[0];
            setResidentCheckInStatusObj(result);
            let isCheckedIn = false;
            if (result?.column && result.column !== 'other' && result.column !== 'not_checked_in') {
                isCheckedIn = true;
            }
            setCheckInStatus(isCheckedIn);
            return isCheckedIn;
        } catch (error) {
            if (!quiet) sendToast('error', error instanceof Error ? error.message : 'Error while checking the check-in status');
            return false;
        }
    };

    const handleCheckInClick = async () => {
        if (isFetchingStatus || isCheckInStatusLoading) {
            return;
        }
        if (checkInStatus) {
            sendToast('info', 'Already checked in for the day');
            return;
        }
        if (activeResident && activeResident._id) {
            setIsFetchingStatus(true);
            try {
                if (facilityTimeZone) {
                    const isCheckedIn = await fetchCheckInStatus(facilityTimeZone);
                    if (isCheckedIn) {
                        sendToast('info', 'Already checked in for the day');
                        setIsFetchingStatus(false);
                        return;
                    }
                }
            } catch (error) {
                sendToast('error', 'Error checking check-in status');
                setIsFetchingStatus(false);
                return;
            }
            setIsFetchingStatus(false);
        }
        setShowCheckInConfirm(true);
    };

    const handleCheckInConfirm = async () => {
        if (!activeResident || !activeResident._id) {
            sendToast('error', 'Selected resident data is missing or incomplete. Please contact support.');
            setShowCheckInConfirm(false);
            return;
        }
        if (checkInStatus) {
            // Already checked in (another tab or device got there first): nothing to send.
            sendToast('info', 'Already checked in for the day');
            setShowCheckInConfirm(false);
            return;
        }
        const payload: CheckInPayload = {
            column: 'checked_in',
            registrant: {
                _id: String(activeResident._id),
                FirstName: activeResident.FirstName || '',
                LastName: activeResident.LastName || '',
            },
            Facility: activeResident.Facility || '',
            registrantId: String(activeResident._id),
            roomName: activeResident.Unit?.Name || '',
            roomId: activeResident.Unit?.a4hRoomId || '',
            timestamp: Date.now(),
        };
        try {
            setIsCheckInStatusLoading(true);
            if (!residentCheckInStatusObj) {
                const response = await CheckIn.createCheckInCard(payload);
                if (response.status === 'success') {
                    // greetingResult is the authoritative outcome from sendGreetingsToContacts,
                    // forwarded as-is on this response. Mirrors neighbors-greetings/index.tsx's own
                    // Hello! feedback so the two surfaces agree on what each reason means.
                    const { outcome } = getCheckInOutcome('create', response);
                    if (outcome === 'already_checked_in') {
                        // A card for today already existed (or create moved it), so this press sent nothing.
                        // Re-read the card before saying anything: only claim "already checked in" if it is.
                        // Quiet: when this read fails the warning below is the one message the resident gets.
                        const confirmed = facilityTimeZone ? await fetchCheckInStatus(facilityTimeZone, true) : false;
                        if (confirmed) {
                            sendToast('info', 'Already checked in for the day');
                        } else {
                            sendToast('warn', "We couldn't confirm your check-in. Please check your status and try again.");
                        }
                    } else {
                        setCheckInStatus(true);
                        if (outcome === 'not_signed_up' && !greetingsSummary.cancelAtPeriodEnd) {
                            setGreetingNotice('not_signed_up');
                        } else if (outcome === 'not_signed_up' || outcome === 'switched_off') {
                            // A cancellation waiting for period end switches Greetings off at once, so the
                            // server answers "not entitled": for this resident that means switched off.
                            setGreetingNotice('switched_off');
                        } else if (outcome === 'undeliverable') {
                            sendToast('warn', "Checked in, but your greeting wasn't sent. None of your contacts can receive it (opted out, or a number that can't get texts).");
                        } else if (outcome === 'send_failed') {
                            sendToast('warn', "Checked in, but your greeting wasn't sent.");
                        } else if (outcome === 'no_recipients') {
                            sendToast(
                                'warn',
                                <span>
                                    Checked in, but your greeting wasn&apos;t sent — you have no contacts set up to
                                    receive greetings.{' '}
                                    <a
                                        href="/greetings"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            history.push('/greetings', { scrollTo: 'contacts' });
                                        }}
                                    >
                                        Choose who gets your messages
                                    </a>
                                    .
                                </span>,
                            );
                        } else {
                            // sent / not_enabled / unknown: the check-in worked.
                            sendToast('success', 'Successfully checked in for the day');
                        }
                    }
                }
            } else {
                const updatedCheckInStatusResponse: CheckInPayload = {
                    ...residentCheckInStatusObj,
                    column: 'checked_in',
                    prev_col: residentCheckInStatusObj.column,
                };
                const response = await CheckIn.updateCheckinCard(updatedCheckInStatusResponse);
                if (response.status === 'success') {
                    setResidentCheckInStatusObj(updatedCheckInStatusResponse);
                    setCheckInStatus(true);
                    // Card moved into checked_in via /checkin/update -- no greeting
                    // is attempted on this path (see neighbors-greetings/index.tsx),
                    // so say only what actually happened.
                    sendToast('success', 'Checked in for the day.');
                }
            }
        } catch (error) {
            sendToast('error', 'Error while updating the check in status');
        } finally {
            setIsCheckInStatusLoading(false);
            setShowCheckInConfirm(false);
        }
    };

    const handleCheckInCancel = () => {
        if (!isCheckInStatusLoading) {
            setShowCheckInConfirm(false);
        }
    };

    if (!activeResident) {
        return <Message content="Invalid user. Please contact support." />;
    }

    const renderTile = (option: NeighborsHomeOption) => {
        const colorClass = option.barColor === BLUE_BAR ? 'blue' : 'green';

        const tileContent = (
            <>
                <div className="home-tile-icon-wrap">
                    <option.homeIcon />
                </div>
                <span className="home-tile-label">{option.name}</span>
                <span className="home-tile-sublabel">{option.homeSublabel}</span>
            </>
        );

        if (option.path === HelloOption.path) {
            const helloBusy = isFetchingStatus || isCheckInStatusLoading;
            // The band and badge only appear once both lookups answered. While loading, on
            // failure, or for a guest the tile is plain Hello! -- never an upsell, so a
            // paying family is not told to sign up because a request failed.
            const greetingsReady = greetingsSummary.status === 'ready';
            // A subscription waiting to cancel at period end already has Greetings switched off
            // (the backend clears the flag on any cancel), so the tile reads as off.
            const greetingsOn = greetingsReady && greetingsSummary.subscribed && !greetingsSummary.cancelAtPeriodEnd;
            // With Greetings on, Hello! opens the Greetings page (its own Hello! button does the check-in
            // and the greeting); otherwise the tile runs the plain check-in.
            const openHello = () => {
                // Until the answer is in, a press could check in from a tile that is about to become
                // the Greetings one.
                if (greetingsSummary.status === 'loading' || greetingsAnswerPending) return;
                if (greetingsOn) {
                    history.push('/greetings');
                } else {
                    handleCheckInClick();
                }
            };
            const shownRecipients = greetingsSummary.recipients.slice(0, 3);
            return (
                <div
                    key={option.path}
                    className={`home-tile home-tile--hello ${colorClass}${greetingsOn ? ' home-tile--greetings-on' : ''}`}
                >
                    <div
                        className="home-tile-main"
                        role="button"
                        tabIndex={0}
                        style={{ cursor: !greetingsOn && helloBusy ? 'not-allowed' : 'pointer' }}
                        onClick={openHello}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openHello();
                            }
                        }}
                    >
                        {((!greetingsOn && (helloBusy || isFetchingFacilityTimeZoneLoading)) || greetingsSummary.status === 'loading' || greetingsAnswerPending) && (
                            <Dimmer active style={{ background: 'rgba(255, 255, 255, 0.4)' }}>
                                <Loader />
                            </Dimmer>
                        )}
                        <div className="home-tile-icon-wrap">
                            <option.homeIcon />
                        </div>
                        <span className="home-tile-label">{option.name}</span>
                        <span className="home-tile-sublabel">
                            {greetingsOn ? 'Check in & greet your family' : option.homeSublabel}
                        </span>
                        {greetingsOn && shownRecipients.length > 0 && (
                            <span className="home-tile-recipients">
                                {shownRecipients.map((recipient) => (
                                    <span
                                        key={recipient.id}
                                        className="home-tile-recipient-avatar"
                                        title={recipient.name || undefined}
                                        aria-hidden="true"
                                    >
                                        {recipient.initials}
                                    </span>
                                ))}
                                <span className="home-tile-recipient-count">{greetingsSummary.recipients.length} family</span>
                            </span>
                        )}
                    </div>
                    {greetingsReady && !greetingsSummary.subscribed && (
                        // Its own zone: highlights on its own and opens the Greetings page, while the rest
                        // of the tile runs the check-in.
                        <button type="button" className="home-tile-greetings-band" onClick={() => history.push('/greetings')}>
                            <HeartGlyph size={12} />
                            Add Greetings to tell your family too
                        </button>
                    )}
                    {greetingsReady && greetingsSummary.subscribed && greetingsSummary.cancelAtPeriodEnd && (
                        // Subscribed but waiting to cancel: not an upsell, the page is where to keep it.
                        <button type="button" className="home-tile-greetings-band" onClick={() => history.push('/greetings')}>
                            <HeartGlyph size={12} />
                            Greetings is switched off. Tap to keep it
                        </button>
                    )}
                    {greetingsOn && (
                        <span className="home-tile-greetings-badge">
                            <HeartGlyph size={9} />
                            Greetings
                        </span>
                    )}
                </div>
            );
        }

        if (option.path === SocialOption.path) {
            return (
                <Link key={option.path} to={option.path} className={`home-tile ${colorClass}`}>
                    <button
                        type="button"
                        className={`home-tile-badge ${isSocialDefault ? 'active' : ''}`}
                        aria-pressed={isSocialDefault}
                        aria-label={
                            isSocialDefault
                                ? 'Remove Social as your favorite page'
                                : 'Set Social as your favorite page'
                        }
                        title={
                            isSocialDefault
                                ? 'Social is your favorite page and will open by default'
                                : 'Star Social to open it by default'
                        }
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSocialDefault();
                        }}
                        disabled={isSavingDefaultLanding}
                    >
                        <StarIcon size={20} />
                    </button>
                    {tileContent}
                </Link>
            );
        }

        return (
            <Link key={option.path} to={option.path} className={`home-tile ${colorClass}`}>
                {tileContent}
            </Link>
        );
    };

    return (
        <div className="neighbors-home">
            <div className="neighbors-home-content">
                <div className="neighbors-home-sidebar">
                    <Greeting firstName={getResidentDisplayFirstName(activeResident)} />
                    <News facilityTimezone={facilityTimeZone} parentLoading={isFetchingFacilityTimeZoneLoading} />
                    <Status facilityTimezone={facilityTimeZone} parentLoading={isFetchingFacilityTimeZoneLoading} />
                </div>

                <section className="tiles-section">
                    <div className="section-label">Quick Access</div>
                    <div className="tiles-grid">
                        {visibleHomeGridOptions.map(renderTile)}
                        {canTogglePremise && (
                            <div
                                className={`home-tile ${onPremise === false ? 'premise-tile--out' : 'green'}${isPremiseBusy || onPremise === undefined ? ' premise-tile--loading' : ''}`}
                                role="button"
                                tabIndex={0}
                                aria-disabled={isPremiseBusy}
                                style={{ cursor: isPremiseBusy ? 'not-allowed' : 'pointer' }}
                                onClick={handlePremiseClick}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handlePremiseClick();
                                    }
                                }}
                            >
                                <div className="home-tile-icon-wrap">
                                    <i className={`fa-light ${onPremise === false ? 'fa-right-from-bracket' : 'fa-right-to-bracket'}`} aria-hidden="true" />
                                </div>
                                <span className="home-tile-label">{onPremise === undefined ? 'Check status' : onPremise ? "I'm In" : "I'm Out"}</span>
                                <span className="home-tile-sublabel">
                                    {onPremise === undefined ? 'Tap to check' : onPremise ? 'Tap to sign out' : 'Tap to sign in'}
                                </span>
                            </div>
                        )}
                    </div>
                </section>
            </div>
            <SignOutQuestionsModal
                open={!!signOutPrompt}
                questions={signOutPrompt?.items ?? []}
                submitting={isPremiseUpdating}
                locked={answersSaved}
                onCancel={closeSignOutPrompt}
                onSubmit={handleSignOutSubmit}
            />
            {greetingNotice && (
                <CheckInNotice
                    title="You're checked in to your community - but your family wasn't notified"
                    body={
                        greetingNotice === 'not_signed_up'
                            ? "Daily Greetings isn't turned on for your account yet."
                            : 'Daily Greetings is switched off for your account.'
                    }
                    actionLabel={greetingNotice === 'not_signed_up' ? 'Learn about Greetings' : 'Open Greetings'}
                    onAction={() => {
                        setGreetingNotice(null);
                        history.push('/greetings');
                    }}
                    onClose={closeGreetingNotice}
                />
            )}
            <Modal
                open={showCheckInConfirm}
                onClose={handleCheckInCancel}
                className="check-in-confirm-modal"
                closeOnDimmerClick={!isCheckInStatusLoading}
                closeOnEscape={!isCheckInStatusLoading}
            >
                <Modal.Content className="check-in-confirm-body">
                    <div className="check-in-confirm-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 11l3 3L22 4" />
                            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                        </svg>
                    </div>
                    <div className="check-in-confirm-title">Are you sure you want to check in for the day?</div>
                </Modal.Content>
                <Modal.Actions className="check-in-confirm-actions">
                    <button
                        type="button"
                        className="check-in-confirm-btn check-in-confirm-btn-cancel"
                        disabled={isCheckInStatusLoading}
                        onClick={handleCheckInCancel}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="check-in-confirm-btn check-in-confirm-btn-yes"
                        disabled={isCheckInStatusLoading}
                        onClick={handleCheckInConfirm}
                    >
                        {isCheckInStatusLoading && <span className="check-in-confirm-spinner" />}
                        Yes
                    </button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default NeighborsHome;
